import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useCollectionFreshness } from '../hooks/useCollectionFreshness';
import { reloadClientConfigs, getConfigCache, isConfigLoaded } from '../services/clientConfigService';

type ConfigValue = string | number | boolean | object;

interface ConfigContextType {
  configs: Record<string, ConfigValue>;
  getConfig: <T = string>(key: string, defaultValue?: T) => T;
  getConfigOptional: <T = string>(key: string) => T | undefined;
  isLoaded: boolean;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

function getEnvOverride(key: string): string | number | boolean | undefined {
  const envName = 'VITE_' + key.toUpperCase().replace(/\./g, '_');
  const envValue = import.meta.env[envName] as string | undefined;
  if (envValue === undefined || envValue === '') return undefined;
  if (envValue === 'true') return true;
  if (envValue === 'false') return false;
  const numValue = Number(envValue);
  if (!isNaN(numValue) && envValue.trim() !== '') return numValue;
  return envValue;
}

export function ConfigProvider({ children }: { children: ReactNode }) {
  const { authState } = useAuth();
  const token = authState.token;
  const isAuthenticated = authState.isAuthenticated;

  const [configs, setConfigs] = useState<Record<string, ConfigValue>>(() => getConfigCache());
  const [loaded, setLoaded] = useState(() => isConfigLoaded());
  const lastTimestampRef = useRef<string | null>(null);
  const initialLoadDoneRef = useRef(false);

  const metadataTimestamps = useCollectionFreshness(['system_config'], {
    interval: 30_000,
    enabled: isAuthenticated,
  });

  // Sync context state when AuthContext's initial loadClientConfigs completes
  useEffect(() => {
    if (loaded || !token) return;

    const checkInterval = setInterval(() => {
      if (isConfigLoaded()) {
        setConfigs(getConfigCache());
        setLoaded(true);
        clearInterval(checkInterval);
      }
    }, 200);

    return () => clearInterval(checkInterval);
  }, [loaded, token]);

  // React to metadata timestamp changes — reload configs when backend signals a change
  useEffect(() => {
    if (!token) return;

    const ts = metadataTimestamps['system_config'];
    if (!ts) return;

    // Skip the very first timestamp (initial load, not a change)
    if (!initialLoadDoneRef.current) {
      initialLoadDoneRef.current = true;
      lastTimestampRef.current = ts;
      return;
    }

    if (ts === lastTimestampRef.current) return;
    lastTimestampRef.current = ts;

    reloadClientConfigs(token).then(() => {
      setConfigs(getConfigCache());
      setLoaded(true);
      console.log('[ConfigProvider] Configs refreshed from backend');
    }).catch(err => {
      console.warn('[ConfigProvider] Failed to reload configs:', err);
    });
  }, [metadataTimestamps, token]);

  const getConfigValue = useCallback(<T = string>(key: string, defaultValue?: T): T => {
    const envValue = getEnvOverride(key);
    if (envValue !== undefined) return envValue as T;
    if (key in configs) return configs[key] as T;
    if (defaultValue !== undefined) return defaultValue;
    throw new Error(`Config not found: ${key}`);
  }, [configs]);

  const getConfigOptionalValue = useCallback(<T = string>(key: string): T | undefined => {
    const envValue = getEnvOverride(key);
    if (envValue !== undefined) return envValue as T;
    if (key in configs) return configs[key] as T;
    return undefined;
  }, [configs]);

  return (
    <ConfigContext.Provider value={{ configs, getConfig: getConfigValue, getConfigOptional: getConfigOptionalValue, isLoaded: loaded }}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useClientConfig(): ConfigContextType {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useClientConfig must be used within a ConfigProvider');
  }
  return context;
}
